import semver from 'semver'
import get from 'lodash/get'
import uniq from 'lodash/uniq'
import pluralize from 'pluralize'
import { all, latest, matching, resolve, KITS_STARTING_VERSION, CUSTOM_SUBSET_STARTING_VERSION, SUBSET_BY_STYLE_STARTING_VERSION } from 'app/releases'

import AVATAR_CHOICES from './kitIcons'

if (AVATAR_CHOICES.length !== 31) {
  throw new Error('The AVATAR_CHOICES need 31 elements to work with our avatar picker')
}

export function createEmbedCode(kit) {
  const attributes = [
    ['src', `https://${FONTAWESOME.CLOUDS_KIT_HOST}/${kit.token}.js`], // eslint-disable-line no-undef
    ['crossorigin', 'anonymous']
  ]

  if (kit.useIntegrityHash) {
    attributes.push(['integrity', kit.integrityHash])
  }

  const pairs = attributes.map(([key, val]) => `${key}="${val}"`)

  return `<script ${pairs.join(' ')}></script>`
}

export function createEmbedCodeCss(kit) {
  const attributes = [
    ['rel', 'stylesheet'],
    ['href', `https://${FONTAWESOME.CLOUDS_KIT_HOST}/${kit.token}.css`], // eslint-disable-line no-undef
    ['crossorigin', 'anonymous']
  ]

  if (kit.useIntegrityHash) {
    attributes.push(['integrity', kit.integrityHash])
  }

  const pairs = attributes.map(([key, val]) => `${key}="${val}"`)

  return `<link ${pairs.join(' ')}>`
}

export function avatarForToken(token) {
  // This can range from 0 to 30. The tokens are hexidecimal so 0 = 0 to f = 15.
  // The maximum is 15 + 15 or 30. Since we are using it to grab an array we need 31 elements in there.
  return AVATAR_CHOICES[parseInt(token[0], 16) + parseInt(token[1], 16)]
}

export function toIconDefinition(iconThing) {
  if (iconThing.name && iconThing.kitId) {
    // Looks like a uploaded icon
    const pathData = iconThing.pathSecondary ? [iconThing.pathSecondary, iconThing.path] : iconThing.path

    return {
      iconName: iconThing.name,
      prefix: FONTAWESOME.CLOUDS_KIT_PREFIX,
      icon: [
        iconThing.width,
        iconThing.height,
        [], // ligatures
        iconThing.unicode,
        pathData
      ]
    }
  }

  return {}
}

const allSupportingKits = all.together.filter((r) => semver.gte(r.version, KITS_STARTING_VERSION))

export function versionDeterminations({ selectedVersion, permitted }) {
  // Treat "latest" as 5.x (YES VERSION FIVE)
  if (selectedVersion === 'latest') {
    selectedVersion = '5.x'
  }

  const parsedVersion = semver.parse(selectedVersion)
  const majorVersion = get(parsedVersion, ['major'], parseInt(selectedVersion.slice(0, 1), 10))
  const isSpecific = !!parsedVersion
  const isPrerelease = get(parsedVersion, ['prerelease', 'length'], 0) > 0

  let versions = allSupportingKits.map((v) => v.version)

  // filter by either CUSTOM_SUBSET_STARTING_VERSION or the current version, which ever is greater
  const compareVersion = kitVersionStringToVersionString(selectedVersion)
  const customSubsetVersions = filterVersionsList(semver.gte(CUSTOM_SUBSET_STARTING_VERSION, compareVersion) ? CUSTOM_SUBSET_STARTING_VERSION : compareVersion)

  const styleSubsetVersions = filterVersionsList(SUBSET_BY_STYLE_STARTING_VERSION)

  const majorVersions = allSupportingKits
    .reduce((acc, v) => {
      const {
        versionParts: { major }
      } = v
      if (acc.indexOf(major) === -1) {
        acc.push(major)
      }
      return acc
    }, [])
    .sort()
    .reverse()

  const latestMajorVersion = majorVersions[0]
  const majorSubsettableVersions = majorVersions.filter((v) => v >= 6)

  let isAvailable = true

  if (isSpecific) {
    isAvailable = versions.indexOf(selectedVersion) > -1
  } else {
    isAvailable = majorVersions.map((m) => `${m}.x`).indexOf(selectedVersion) > -1
  }

  versions = uniq(versions).sort(semver.compare).reverse()

  return {
    selected: {
      version: selectedVersion,
      majorVersion
    },
    isSpecific,
    isPrerelease,
    isAvailable,
    available: {
      versions,
      majorVersions,
      latestMajorVersion,
      majorSubsettableVersions,
      customSubsetVersions,
      styleSubsetVersions
    }
  }
}

export function filterVersionsList(minimumVersion) {
  return allSupportingKits
    .reduce((acc, v) => {
      if (semver.gte(v.version, minimumVersion)) {
        acc.push(v.version)
      }
      return acc
    }, [])
    .sort()
    .reverse()
}

export function kitVersionStringToVersion(versionString) {
  switch (versionString) {
    case '5.x': //latest v5
      return matching({ version: latest.v5.version })
    case '6.x': //latest v6
      return matching({ version: latest.v6.version })
    case '7.x': //latest v7
      return matching({ version: latest.v7.version })
    case 'latest': //latest should return the latest version five
      return matching({ version: latest.v5.version })
    default:
      return matching({ version: versionString })
  }
}

export function kitVersionStringToVersionString(versionString) {
  switch (versionString) {
    case '5.x': //latest v5
      return latest.v5.version
    case '6.x': //latest v6
      return latest.v6.version
    case '7.x': //latest v7
      return latest.v7.version
    case 'latest': //latest should return the latest version five
      return latest.v5.version
    default:
      return resolve({ version: versionString })
  }
}

export function kitVersionLabel(version) {
  switch (version) {
    case 'latest':
      return 'Latest Version 5'
    case '5.x':
      return 'Latest Version 5'
    case '6.x':
      return 'Latest Version 6'
    case '7.x':
      return 'Latest Version 7'
    default:
      return `Version ${version}`
  }
}

export function isEverGreenVersion(version) {
  return ['latest', '5.x', '6.x', '7.x'].indexOf(version) > -1
}

export function getKitIconCount(kit) {
  if (kit.subset && kit.subset.icons) {
    return kit.subset.icons.reduce((acc, subset) => {
      return 'only' in subset ? acc + subset.only.length : acc
    }, 0)
  } else {
    // Kit is not subsetted
    return 0
  }
}

export function getKitIconCountByFamilyStyle(kit, family, style) {
  if (style === 'duotone') {
    family = 'duotone'
    style = 'solid'
  }

  if (style === 'brands') {
    family = 'classic'
  }

  if (kit.subset && kit.subset.icons) {
    const found = kit.subset.icons.find((s) => s.family == family && s.style == style)
    return found && found.only ? found.only.length : 0
  } else {
    return 0
  }
}

export function getKitStyleCount(kit) {
  // Returns count of styles based on the subset of a given kit
  if (kit.subset && Array.isArray(kit.subset.icons)) {
    return kit.subset.icons.length // each item in icons array is a subsetted style
  } else {
    // Kit is not subsetted
    return 0
  }
}

export function styleCount(kit) {
  // Returns count of styles selected based on the version of a given kit
  if (kit.licenseSelected === 'pro') {
    const release = kitVersionStringToVersion(kit.version)
    if (release) {
      return Object.keys(release.familyStyleCounts).reduce((cur, family) => {
        return Object.keys(release.familyStyleCounts[family] || {}).length + cur
      }, 0)
    }
    return 0
  } else {
    return 2 // Free
  }
}

export function styleCountLabel(kit) {
  const count = kit.subset ? getKitStyleCount(kit) : styleCount(kit)
  return `${count} ${pluralize('Style', count)}`
}

export function activeProSubscriber(account) {
  return !account?.isFree && account?.subscription?.status === 'active'
}

export function getFamilyStyleIconCount(license, family, style) {
  const release = matching({ version: latest.together.version })
  if (license === 'pro') {
    return release.familyStyleCounts[family][style].pro
  } else {
    return release.familyStyleCounts[family][style].free
  }
}
